import { FC, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Button, InfoBlock, Modal } from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { AuthContext, EndWorkShiftModal } from 'auth';
import { LocalizationContext } from 'localization';
import {
  GET_WORKSHIFT_MONEY,
  IGetWorkShiftMoneyRequest,
  IGetWorkShiftMoneyResponse,
} from 'schemas';

import s from './WorkShiftChecker.module.scss';

export const WorkShiftChecker: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data: workShiftResponse } = useQuery<
    IGetWorkShiftMoneyResponse,
    IGetWorkShiftMoneyRequest
  >(GET_WORKSHIFT_MONEY, {
    fetchPolicy: 'cache-only',
    errorPolicy: 'ignore',
    skip: !permissions?.has('work_shift_active'),
  });
  const created_at =
    workShiftResponse?.getWorkShiftPaymentOverviewData?.created_at;

  const [isEndWorkShiftOpen, setIsEndWorkShiftOpen] = useState(false);
  const [smallViewport, setSmallViewport] = useState<boolean>(false);

  useEffect(() => {
    const updateVisiblePages = (): void => {
      const width = window.innerWidth;
      if (width > 960) {
        setSmallViewport(false);
      } else {
        setSmallViewport(true);
      }
    };

    updateVisiblePages();
    window.addEventListener('resize', updateVisiblePages);
    return () => window.removeEventListener('resize', updateVisiblePages);
  }, []);

  if (!created_at) {
    return null;
  }

  const createdAt = dayjs(created_at);
  const diffFromNow = dayjs().diff(createdAt, 'hours');

  if (diffFromNow < 25) {
    return null;
  }

  if (diffFromNow < 144) {
    return (
      <InfoBlock
        iconName="InfoIcon"
        title={t('workShifts.notify.title')}
        message={t('workShifts.notify.description')}
        direction={smallViewport ? 'column' : 'row'}
      />
    );
  }

  return (
    <div>
      {diffFromNow >= 168 && !isEndWorkShiftOpen && (
        <Modal
          disableCloseByEsc
          hideCloseIcon
          disableOutsideClose
          className={s.modal}
        >
          <div className={s.content}>
            <header className={s.header}>{t('workShifts.long.title')}</header>
            <span>{t('workShifts.long.description')}</span>
          </div>
          <Button
            fullWidth
            onClick={() => {
              setIsEndWorkShiftOpen(true);
            }}
          >
            {t('workShifts.long.submit')}
          </Button>
        </Modal>
      )}
      {isEndWorkShiftOpen && (
        <EndWorkShiftModal onClose={() => setIsEndWorkShiftOpen(false)} />
      )}
      <InfoBlock
        iconName="FinishIcon"
        color="red"
        title={t('workShifts.long.title')}
        message={t(
          'workShifts.notify.finish.description',
          createdAt.format('HH:mm')
        )}
        direction={smallViewport ? 'column' : 'row'}
      />
    </div>
  );
};
